/* eslint-disable max-lines */
import Konva from 'konva';
import { memo, useCallback, useMemo, useRef } from 'react';
import { Group, Line, Path, Text } from 'react-konva';
import { MidpointHandle } from './MidpointHandle';
import { VertexHandle } from './VertexHandle';
import { CanvasDimensions, Point, SpecialZoneType } from './types';
import { useCursorHandlers } from './useCursorHandlers';
import { useZoneDragHandlers } from './useZoneDragHandlers';
import {
  calculateMidpoints,
  getPolygonIconPlacement,
  getZoneIconScale,
  getZonePatternImage,
  pointsToFlatArray,
  ZONE_HALO_STROKE_COLOR,
  ZONE_ICON_CONFIGS,
  ZONE_PATTERN_CONFIGS,
} from './utils';

interface Props {
  zoneId: string;
  points: Point[];
  bounds: CanvasDimensions;
  onPointsChange: (zoneId: string, points: Point[]) => void;
  type: SpecialZoneType;
  /** Drawn under the icon, for zones that have a name */
  label?: string;
  isSelected: boolean;
  onSelect: (zoneId: string) => void;
  canEdit: boolean;
}

const LABEL_WIDTH = 160;
const LABEL_FONT_SIZE = 13;

export const ZonePolygon = memo(function ZonePolygon({
  zoneId,
  points,
  bounds,
  onPointsChange,
  type,
  label,
  isSelected,
  onSelect,
  canEdit,
}: Props) {
  const groupRef = useRef<Konva.Group>(null);
  const lineRef = useRef<Konva.Line>(null);

  const midpoints = useMemo(() => calculateMidpoints(points), [points]);
  const flatPoints = useMemo(() => pointsToFlatArray(points), [points]);

  const patternConfig = ZONE_PATTERN_CONFIGS[type];
  const patternImage = getZonePatternImage(type);

  const iconConfig = ZONE_ICON_CONFIGS[type];
  const { center: iconCenter, radius: inscribedRadius } = useMemo(
    () => getPolygonIconPlacement(points),
    [points]
  );
  const iconScale = useMemo(
    () => getZoneIconScale(inscribedRadius),
    [inscribedRadius]
  );

  const handleZonePointsChange = useCallback(
    (newPoints: Point[]) => {
      onPointsChange(zoneId, newPoints);
    },
    [zoneId, onPointsChange]
  );

  const {
    handleDragStart,
    handleVertexDragMove,
    handleVertexDragEnd,
    handleVertexDoubleClick,
    handleMidpointDragMove,
    handleMidpointDragEnd,
    handlePolygonDragEnd,
  } = useZoneDragHandlers({
    points,
    bounds,
    onPointsChange: handleZonePointsChange,
    groupRef,
    lineRef,
  });

  const { crosshairCursorEnter, resizeCursorEnter, handleCursorLeave } =
    useCursorHandlers();

  const handleClick = useCallback(() => {
    if (canEdit) {
      onSelect(zoneId);
    }
  }, [zoneId, onSelect, canEdit]);

  const handleDragStartWithSelect = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>) => {
      onSelect(zoneId);
      handleDragStart(e);
    },
    [zoneId, onSelect, handleDragStart]
  );

  return (
    <Group
      ref={groupRef}
      draggable={canEdit}
      onDragStart={handleDragStartWithSelect}
      onDragEnd={handlePolygonDragEnd}
      onClick={handleClick}
      onTap={handleClick}
    >
      {/* Outer halo stroke — subtle white edge for contrast on dark backgrounds */}
      <Line
        points={flatPoints}
        stroke={ZONE_HALO_STROKE_COLOR}
        strokeWidth={isSelected ? 3 : 2}
        closed
        listening={false}
      />
      {/* Main zone: pattern fill + colored inner stroke. Konva accepts
          HTMLCanvasElement here at runtime, but its config type is narrowed
          to HTMLImageElement — cast accordingly. */}
      <Line
        ref={lineRef}
        points={flatPoints}
        fillPatternImage={patternImage as unknown as HTMLImageElement}
        fillPatternRepeat="repeat"
        stroke={patternConfig.strokeColor}
        strokeWidth={isSelected ? 1.5 : 1}
        closed
      />
      {/* Centered zone type icon — placed at the polygon's pole of
          inaccessibility and sized to the inscribed circle, so it stays
          inside the shape even for irregular/concave polygons. */}
      {iconScale > 0 && (
        <Path
          x={iconCenter.x - 12 * iconScale}
          y={iconCenter.y - 12 * iconScale}
          data={iconConfig.path}
          fill={iconConfig.fill}
          opacity={iconConfig.opacity}
          scaleX={iconScale}
          scaleY={iconScale}
          listening={false}
        />
      )}
      {/* The zone's name, under its icon. Haloed so it reads on any image. */}
      {label && inscribedRadius > 0 && (
        <Text
          x={iconCenter.x - LABEL_WIDTH / 2}
          y={iconCenter.y + 12 * iconScale + 4}
          width={LABEL_WIDTH}
          align="center"
          text={label}
          fontSize={LABEL_FONT_SIZE}
          fontStyle="bold"
          fill="#ffffff"
          shadowColor="rgba(0, 0, 0, 0.7)"
          shadowBlur={4}
          listening={false}
        />
      )}
      {/* Only render handles when zone is selected */}
      {isSelected && (
        <>
          {/* Render midpoints first so vertices appear on top */}
          {midpoints.map((midpoint, index) => (
            <MidpointHandle
              key={`midpoint-${index}`}
              position={midpoint}
              index={index}
              onDragStart={handleDragStart}
              onDragMove={handleMidpointDragMove}
              onDragEnd={handleMidpointDragEnd}
              onMouseEnter={crosshairCursorEnter}
              onMouseLeave={handleCursorLeave}
            />
          ))}
          {points.map((point, index) => (
            <VertexHandle
              key={`vertex-${index}`}
              point={point}
              index={index}
              onDragStart={handleDragStart}
              onDragMove={handleVertexDragMove}
              onDragEnd={handleVertexDragEnd}
              onDoubleClick={handleVertexDoubleClick}
              onMouseEnter={resizeCursorEnter}
              onMouseLeave={handleCursorLeave}
            />
          ))}
        </>
      )}
    </Group>
  );
});

export default ZonePolygon;
